// The real rungs of the delivery ladder (docs/10 §5).
//
// Fingerprint discipline (§8) is a constraint on every one of them: the upload
// lives under the existing `/api/v1` shape rather than on a distinctive new
// path, carries no custom headers, and rides the same TLS on the same port as
// everything else. A diagnostics feature that makes the app easier to block
// would be a net loss.
//
// Uploads are UNAUTHENTICATED (§3). Attaching a bearer token would let the
// server link a log stream to an account, which is precisely the correlation
// this design exists to prevent — so no sender here ever touches
// `getAccessToken`, and none of them may be given a fetch that injects one.

import { httpFailureKind } from '../../lib/http.js';
import type { DeliveryOutcome, DeliveryVia } from './report.js';
import type { DiagSender, UploadPayload } from './uploader.js';

/** Route beneath the API version prefix. Ordinary shape, no new surface. */
export const DIAG_REPORT_PATH = '/diag/reports';

/** Classify a delivery attempt with the same vocabulary as everything else —
 *  codes and kinds, never a server sentence. */
export function outcomeOfResponse(status: number): DeliveryOutcome {
  return status >= 200 && status < 300 ? 'ok' : 'status';
}

export function outcomeOfError(err: unknown): DeliveryOutcome {
  const kind = httpFailureKind(err);
  if (kind === 'timeout') return 'timeout';
  if (kind === 'rejected') return 'rejected';
  if (kind === 'frozen') return 'frozen';
  if (kind === 'cancelled') return 'unavailable';
  return 'other';
}

function headers(payload: UploadPayload): Record<string, string> {
  return payload.gzip === null
    ? { 'content-type': 'application/json' }
    : { 'content-type': 'application/json', 'content-encoding': 'gzip' };
}

function bodyOf(payload: UploadPayload): BodyInit {
  return (payload.gzip ?? payload.bytes) as BodyInit;
}

async function post(
  fetchFn: typeof fetch,
  url: string,
  payload: UploadPayload,
): Promise<DeliveryOutcome> {
  try {
    const response = await fetchFn(url, {
      method: 'POST',
      headers: headers(payload),
      body: bodyOf(payload),
    });
    return outcomeOfResponse(response.status);
  } catch (err) {
    return outcomeOfError(err);
  }
}

/**
 * Rung 2 — any reachable mirror. `baseUrl` is the brain's REST origin router,
 * so this rides the endpoint failover REST already has: one blocked hostname is
 * not an outage.
 */
export function restSender(deps: {
  fetchFn: typeof fetch;
  baseUrl: () => string;
  path?: string;
}): DiagSender {
  const path = deps.path ?? DIAG_REPORT_PATH;
  return {
    via: 'rest',
    async send(payload: UploadPayload): Promise<DeliveryOutcome | null> {
      const base = deps.baseUrl();
      if (!base) return null;
      return post(deps.fetchFn, `${base}${path}`, payload);
    },
  };
}

/**
 * Rung 3 — a separate intake on independent infrastructure, taken from the
 * `diag` array of the SIGNED endpoint list, so it rotates through the same
 * verified channel as `turn` and `doh`.
 *
 * The structural point is that the intake must not share a failure domain with
 * the API: an intake on merkury.app dies with merkury.app, precisely when it is
 * needed. Which provider actually survives is empirical — ship several, and let
 * the reports themselves say which intake wins, which is why every listed
 * intake is tried in order rather than one being chosen here.
 */
export function intakeSender(deps: {
  fetchFn: typeof fetch;
  intakes: () => readonly { url: string }[];
}): DiagSender {
  return {
    via: 'intake',
    async send(payload: UploadPayload): Promise<DeliveryOutcome | null> {
      const intakes = deps.intakes();
      if (intakes.length === 0) return null;
      let last: DeliveryOutcome | null = null;
      for (const intake of intakes) {
        last = await post(deps.fetchFn, intake.url, payload);
        if (last === 'ok') return last;
      }
      return last;
    },
  };
}

/**
 * Rung 1 — the socket that is already open. The failure we actually hit had a
 * live WSS connection the whole time; a request that rides an established flow
 * pays no new handshake, which is exactly what carriers enforcing at flow SETUP
 * were killing.
 *
 * `rpc` is the seam: it resolves with the tunnelled HTTP status
 * (`sys.rpc.result`, protocol/ws.ts) or rejects. When the signaling client has
 * no socket — or does not implement RPC-over-WS yet — it answers `null` and the
 * ladder steps down without spending an attempt.
 */
export function socketSender(deps: {
  rpc: (request: { method: 'POST'; path: string; body: unknown }) => Promise<number | null>;
  path?: string;
}): DiagSender {
  const path = deps.path ?? DIAG_REPORT_PATH;
  return {
    via: 'ws' as DeliveryVia,
    async send(payload: UploadPayload): Promise<DeliveryOutcome | null> {
      let status: number | null;
      try {
        // The socket carries JSON frames, so the report travels as the parsed
        // document — the gzip is an HTTP transport encoding and has no meaning
        // here. Identical bytes either way: this is `payload.bytes`, parsed.
        status = await deps.rpc({ method: 'POST', path, body: JSON.parse(payload.bytes) });
      } catch (err) {
        return outcomeOfError(err);
      }
      return status === null ? null : outcomeOfResponse(status);
    },
  };
}
